import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { uploadTaskAttachment } from '../lib/api'

/** Mirrors the server's cap in `src/api/task-details.ts` so the rejection
 *  happens before a 10 MB round trip, not after it. */
export const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024

/**
 * Name a file that arrived without one. The clipboard hands over an image blob
 * with an empty `name`, so every pasted screenshot would otherwise land as
 * "image.png" and be indistinguishable in the list.
 */
export function pastedFileName(file: File, now: string): string {
  if (file.name && file.name !== 'image.png') return file.name
  const ext = file.type.split('/')[1]?.replace(/[^a-z0-9]/gi, '') || 'png'
  return `screenshot-${now.replace(/[:.]/g, '-').slice(0, 19)}.${ext}`
}

/**
 * The one upload path for a task's attachments, shared by the Files tab button,
 * clipboard paste, and drag-and-drop. Kept as a hook rather than duplicated per
 * entry point so the size guard and cache invalidation can't drift apart.
 */
export function useTaskAttachmentUpload(taskId: string) {
  const qc = useQueryClient()
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const mutation = useMutation({
    mutationFn: ({ file, filename }: { file: File; filename?: string }) =>
      uploadTaskAttachment(taskId, file, filename),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['task-attachments', taskId] }),
    onError: (err: Error) => setError(err.message),
  })

  /**
   * Upload one or more files, skipping any that blow the size cap.
   * `renameTo` supplies a filename for sources that don't carry one (clipboard).
   */
  const upload = async (files: File[], renameTo?: (file: File) => string) => {
    if (!files.length) return
    const tooBig = files.filter((f) => f.size > MAX_ATTACHMENT_BYTES)
    const ok = files.filter((f) => f.size <= MAX_ATTACHMENT_BYTES)

    setError(
      tooBig.length
        ? `${tooBig.map((f) => f.name || 'file').join(', ')} exceeds the 10MB limit and was not uploaded.`
        : null,
    )
    if (!ok.length) return

    setUploading(true)
    try {
      for (const file of ok) {
        await mutation.mutateAsync({ file, filename: renameTo?.(file) })
      }
    } catch {
      // onError already recorded the message; swallow so one bad file does not
      // abort the whole drop.
    } finally {
      setUploading(false)
    }
  }

  return { upload, uploading, error, clearError: () => setError(null) }
}
